import { SidebarBadgeTone } from './menuConfig'
import { useState, useEffect, useRef, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Tooltip } from 'components/ds2/Tooltip'
import { Badge } from 'components/ds2/Badge'

import { ROW_ACTIVE, ROW_BASE, ROW_HEADER } from './styles'
import { ChevronDown } from './icons'
import { handleLinkClick } from 'utils/linkClick'
import { SidebarChildItem } from './SidebarChildItem'

/** Flat description of one child item used to render the collapsed-rail popover. */
interface SidebarGroupPopoverItem {
    id: string
    label: string
    icon: ReactNode
    active?: boolean
    /** Sub-section header rendered above the first item of each consecutive run sharing this value. */
    section?: string
    /** Disabled rows (paywalled features) — row is muted, only `suffix` is interactive. */
    disabled?: boolean
    badge?: { label: string; tone: SidebarBadgeTone }
    /** Right-aligned slot — typically an "Enable" CTA for paywalled items. */
    suffix?: ReactNode
    /** Real destination, so the row can be opened in a new tab. */
    href?: string
    onClick: () => void
}

interface SidebarGroupProps {
    icon: ReactNode
    label: string
    isOpen: boolean
    /** Whether any child item is currently the active route */
    childActive?: boolean
    /**
     * Flat list of child items — drives BOTH the indent column (sidebar open) and the
     * collapsed-rail popover from the same data, so there's one source of truth for a
     * group's children instead of two parallel representations.
     */
    popoverItems?: SidebarGroupPopoverItem[]
    /**
     * Stable DOM hook on the group header — used by overlays/tooltips to anchor
     * to the group when its children aren't rendered (collapsed group).
     */
    groupId?: string
}

/**
 * A collapsible navigation group used by `updater` and `tmc` roles.
 *
 * Mirrors the reference design's `NavGroup` / `CollapsedNavIcon`
 * (`new-agent-numeo-main/src/components/SidebarComponents.tsx` lines 37–139):
 *
 * - Clicking the header NEVER navigates — it only toggles expand/collapse.
 *   Groups with children only toggle; navigation lives on the children.
 * - Header row uses `ROW_HEADER` (same as `ROW_IDLE`) regardless of whether
 *   a child is active — the reference's `isLeafActive` check makes group
 *   headers skip the accent-soft background.
 * - Chevron is always muted.
 * - `expanded` is a purely manual toggle (default closed) — it is NEVER
 *   auto-forced open just because a child route is active. Instead, while
 *   collapsed, the indent column "peeks" the single active child (reference
 *   lines 124–133: `(isOpen || highlighted) ? children.map(...) :
 *   children.filter(child => child === activeItem).map(...)`). Clicking the
 *   header reveals the rest of the siblings; clicking again re-collapses
 *   back down to just the active-child peek (or fully hidden if none is
 *   active).
 * - When the sidebar is **collapsed** (icon rail), hovering the group icon
 *   opens a floating popover that lists all the children — same affordance
 *   as the reference's `CollapsedNavIcon`, but on hover. The popover is
 *   rendered in a React portal with `position: fixed` because the sidebar's
 *   scrolling `<nav>` clips `overflow-x`, and an absolutely positioned child
 *   would be cut off at the rail edge.
 */
export const SidebarGroup = ({ icon, label, isOpen, childActive, popoverItems, groupId }: SidebarGroupProps) => {
    const [expanded, setExpanded] = useState(false)
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [popoverCoords, setPopoverCoords] = useState<{ top: number; left: number } | null>(null)

    const iconRef = useRef<HTMLDivElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)
    // Small grace period so moving the mouse from the icon onto the popover
    // (across the ~8 px gap) doesn't close it.
    const closeTimerRef = useRef<number | null>(null)

    // Reset the popover whenever the sidebar toggles open — it's meaningless
    // and visually noisy in the expanded layout.
    useEffect(() => {
        if (isOpen) setPopoverOpen(false)
    }, [isOpen])

    // Close popover on click outside — must exclude both the trigger icon and the
    // portal content itself, otherwise mousedown on a popover item unmounts the
    // portal before the subsequent click event can fire child.onClick().
    useEffect(() => {
        if (!popoverOpen) return
        const handleClickOutside = (e: MouseEvent) => {
            if (iconRef.current && !iconRef.current.contains(e.target as Node) && popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
                setPopoverOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [popoverOpen])

    useEffect(
        () => () => {
            if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
        },
        []
    )

    // Reposition the popover if the sidebar's scroll container moves beneath
    // it while it's open. Without this, the popover would float at its initial
    // viewport coordinates while the icon slid away. `capture: true` catches
    // scroll events on ancestors too.
    useEffect(() => {
        if (!popoverOpen) return
        const handler = () => {
            if (!iconRef.current) return
            const rect = iconRef.current.getBoundingClientRect()
            setPopoverCoords({ top: rect.top, left: rect.right + 8 })
        }
        window.addEventListener('scroll', handler, true)
        window.addEventListener('resize', handler)
        return () => {
            window.removeEventListener('scroll', handler, true)
            window.removeEventListener('resize', handler)
        }
    }, [popoverOpen])

    const handleHeaderClick = () => {
        if (!isOpen) {
            // Sidebar is collapsed — a click mirrors hover: toggle the popover.
            // Never navigate here; the children inside the popover do that.
            if (!popoverOpen && iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect()
                setPopoverCoords({ top: rect.top, left: rect.right + 8 })
            }
            setPopoverOpen((prev) => !prev)
            return
        }
        setExpanded((prev) => !prev)
    }

    const headerRow = (
        <button
            type="button"
            onClick={handleHeaderClick}
            data-sidebar-group={groupId}
            data-sidebar-group-expanded={expanded || undefined}
            className={[ROW_BASE, isOpen ? 'justify-between' : 'justify-center', !isOpen && childActive ? ROW_ACTIVE : ROW_HEADER].join(' ')}
        >
            <span className="flex min-w-0 items-center gap-2">
                <span className="flex shrink-0 items-center justify-center">{icon}</span>
                {isOpen && <span className="truncate">{label}</span>}
            </span>
            {isOpen && (
                <span className={`flex shrink-0 items-center transition-transform ${expanded ? '' : '-rotate-90'}`}>
                    <ChevronDown size={12} />
                </span>
            )}
        </button>
    )

    if (!isOpen) {
        const hasPopover = !!popoverItems?.length

        // Show tooltip on all collapsed items when popover is not open
        const iconCell =
            hasPopover && popoverOpen ? (
                headerRow
            ) : (
                <Tooltip content={label} placement="right">
                    {headerRow}
                </Tooltip>
            )

        return (
            <div ref={iconRef} className="shrink-0">
                {iconCell}
                {hasPopover &&
                    popoverOpen &&
                    popoverCoords &&
                    createPortal(
                        <div
                            ref={popoverRef}
                            role="menu"
                            aria-label={label}
                            style={{
                                position: 'fixed',
                                top: popoverCoords.top,
                                left: popoverCoords.left,
                                zIndex: 50,
                                animation: 'sidebarPopoverIn 0.15s ease-out',
                                transformOrigin: 'left center',
                            }}
                            className="min-w-[200px] rounded-2xl border border-border bg-surface p-1 shadow-overlay"
                        >
                            {/*
                             * Popover children are label-only too — matches the
                             * reference's `CollapsedPopoverItem` (lines 350–362)
                             * and keeps the popover visually consistent with the
                             * expanded dropdown's label-only NavChild rows.
                             */}
                            <div className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">{label}</div>
                            <div className="flex flex-col">
                                {popoverItems.map((child, i) => (
                                    <div key={child.id} className="flex flex-col">
                                        {child.section && child.section !== popoverItems[i - 1]?.section && (
                                            <div className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted/70">
                                                {child.section}
                                            </div>
                                        )}
                                        {(() => {
                                            const rowClass = [
                                                'flex h-8 w-full items-center gap-2 rounded-xl pl-3 pr-2 text-sm font-medium transition-colors',
                                                child.disabled
                                                    ? 'cursor-not-allowed text-foreground-muted opacity-50'
                                                    : child.active
                                                      ? 'cursor-pointer bg-accent-soft text-foreground'
                                                      : 'cursor-pointer text-foreground hover:bg-black/5 dark:hover:bg-white/10',
                                            ].join(' ')
                                            const rowBody = (
                                                <>
                                                    <span className="min-w-0 flex-1 truncate text-left">{child.label}</span>
                                                    {/* Badge is suppressed when disabled so the Enable CTA in `suffix` stands alone. */}
                                                    {!child.disabled && child.badge && (
                                                        <Badge variant="soft" type={child.badge.tone} size="sm" label={child.badge.label} />
                                                    )}
                                                    {child.suffix && <span className="flex shrink-0 items-center">{child.suffix}</span>}
                                                </>
                                            )
                                            const pick = () => {
                                                child.onClick()
                                                setPopoverOpen(false)
                                            }
                                            return child.href && !child.disabled ? (
                                                <Link
                                                    to={child.href}
                                                    onClick={(e) => handleLinkClick(e, pick)}
                                                    aria-current={child.active ? 'page' : undefined}
                                                    className={`${rowClass} no-underline`}
                                                >
                                                    {rowBody}
                                                </Link>
                                            ) : (
                                                <div
                                                    role="button"
                                                    tabIndex={child.disabled ? -1 : 0}
                                                    onClick={child.disabled ? undefined : pick}
                                                    onKeyDown={child.disabled ? undefined : (e) => (e.key === 'Enter' || e.key === ' ') && pick()}
                                                    aria-current={child.active ? 'page' : undefined}
                                                    aria-disabled={child.disabled || undefined}
                                                    className={rowClass}
                                                >
                                                    {rowBody}
                                                </div>
                                            )
                                        })()}
                                    </div>
                                ))}
                            </div>
                        </div>,
                        document.body
                    )}
            </div>
        )
    }

    // Collapsed (not manually expanded): peek only the active child, if any — matches the
    // reference's `children.filter(child => child === activeItem)`. Nothing active → empty,
    // and the grid row height below animates fully shut instead of showing a stray gap.
    const visibleChildren = expanded ? (popoverItems ?? []) : (popoverItems ?? []).filter((child) => child.active)

    // 32px per row (h-8) + 2px gap between rows (gap-0.5) + the wrapper's own 2px top margin
    // (mt-0.5). Computed from the actual child count instead of a fixed cap — a fixed `max-h-60`
    // (240px ≈ 7 rows) silently clipped any group with 8+ children with no scroll fallback
    // inside it (safetyGroup already has 7, two px of headroom to spare).
    // Section headers (~24px each incl. their padding) render only while expanded — count the
    // consecutive-run boundaries, or the max-height clips the last rows of a sectioned group.
    const sectionHeaderCount = expanded ? visibleChildren.filter((child, i) => child.section && child.section !== visibleChildren[i - 1]?.section).length : 0
    const expandedHeightPx = visibleChildren.length > 0 ? 2 + visibleChildren.length * 32 + (visibleChildren.length - 1) * 2 + sectionHeaderCount * 24 : 0

    return (
        <div className="flex shrink-0 flex-col">
            {headerRow}
            {/*
             * `max-height` transition, not CSS grid's `grid-template-rows: 0fr/1fr` trick — the
             * grid version was implicated in a right-edge clipping bug on active child rows (no
             * explicit `grid-template-columns` left the single implicit column's width computed
             * ambiguously during the row-track transition). `max-height` only ever affects the
             * vertical axis, so it can't touch child width at all.
             */}
            <div className="overflow-hidden shrink-0 transition-[max-height] duration-200 ease-out" style={{ maxHeight: expandedHeightPx }}>
                {/*
                 * Neutral vertical connector line runs the full height of
                 * the children column. Each `SidebarChildItem` paints its
                 * own 2 px left bar on top of this line with `-ml-px`;
                 * inactive children stay transparent so the neutral line
                 * shows through, active children use `bg-accent` so the
                 * accent color overlays just that child's segment.
                 */}
                <div className="ml-4 mt-0.5 flex flex-col gap-0.5 border-l border-border">
                    {visibleChildren.map((child, i) => (
                        <div key={child.id} className="flex flex-col gap-0.5">
                            {/* Sub-section header above each consecutive run — only while expanded (the
                                collapsed active-child peek would otherwise show a lone header). */}
                            {expanded && child.section && child.section !== visibleChildren[i - 1]?.section && (
                                <div className="pl-4 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground-muted/70">
                                    {child.section}
                                </div>
                            )}
                            <SidebarChildItem
                                label={child.label}
                                active={child.active}
                                disabled={child.disabled}
                                badge={child.badge}
                                suffix={child.suffix}
                                sidebarItemId={child.id}
                                href={child.href}
                                onClick={child.onClick}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
